import { useQuery } from "@rocicorp/zero/react";
import { styled } from "goober";
import { type ReactNode, useState } from "react";
import { queries } from "../../../shared/queries";
import type { SpaceMode } from "../../../shared/schema";
import { formatBytes } from "../../lib/formatBytes";
import { routes, useMakeUrl } from "../../lib/urls";
import { useMonitoringInteraction } from "./analytics";
import { spaceIcon } from "../../lib/spaceIcon";
import { Icon, type IconName } from "../Icon";
import { SmallWorkflowBadge } from "../SmallWorkflowBadge";
import { Treemap } from "../Treemap";
import { WorkflowRetentionModal } from "../WorkflowSettings";
import { PanelTransition } from "../PanelTransition";
import { ModalBody } from "./MonitoringDetailCard";
import { PERSONAL_BUCKET } from "./monitoringScope";
import { OTHER_SPACES_KEY } from "./spaceSegments";
import { useMonitoringSpaces } from "./useMonitoringWorkflows";
import { type StorageAction, StorageSidebar } from "./StorageSidebar";
import {
  StorageDeleteConfirm,
  type StorageDeletion,
} from "./StorageDeleteConfirm";
import {
  formatRetention,
  storagePath,
  type StorageUsageLevel,
  type StorageVolumeTarget,
  type StorageWorkflowLink,
} from "./storageUsageLevel";
import { Panel } from "./workflowsLayout";

export const StorageCard = styled(Panel)`
  flex: 1;
  min-height: 0;
  padding: 0;
  gap: 0;
  border-radius: 16px;

  &[data-split="true"] {
    flex-direction: row;
  }
`;

// Matches the sidebar’s header, so the two read as one bar across the card.
export const StorageCardHeader = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  height: 40px;
  padding: 4px 12px 4px 8px;
  flex-shrink: 0;
  border-bottom: 0.5px solid var(--border-alpha);
`;

const Main = styled("div")`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const TreemapArea = styled("div")`
  flex: 1;
  min-height: 0;
  display: flex;
  padding: 12px;
`;

const KEY_ICONS: Record<string, IconName> = {
  live: "database",
  workflow: "product-workflows",
  outside: "box",
  live_volume: "database",
  branch_volume: "git-branch",
  step_test_volume: "microscope",
  run_volume: "activity",
  personal_volume: "message-circle",
  execution: "receipt-text",
  image: "image",
};

function rowIcon(
  view: StorageUsageLevel["view"],
  key: string,
  spaceMode: SpaceMode | undefined
): IconName {
  switch (view) {
    case "space":
      return key === PERSONAL_BUCKET
        ? "circle-user-round"
        : spaceIcon(spaceMode ?? "private");
    case "workflow":
      return "product-workflows";
    case "branch":
      return "git-branch";
    default:
      return KEY_ICONS[key] ?? "database";
  }
}

const SIDEBAR_TITLES: Record<NonNullable<StorageUsageLevel["view"]>, string> = {
  space: "Spaces",
  owner: "Storage",
  workflow: "Workflows",
  kind: "Data",
  branch: "Branches",
  volume: "Volumes",
  files: "Files",
};

export function StorageTiles({
  header,
  level,
  drillPath,
  canDelete,
  canEditRetention,
}: {
  header: ReactNode;
  level: StorageUsageLevel;
  drillPath: (key: string) => string;
  canDelete: (target: StorageVolumeTarget) => boolean;
  canEditRetention: (spaceId: string) => boolean;
}) {
  const makeUrl = useMakeUrl();
  const recordInteraction = useMonitoringInteraction();
  const [workflows] = useQuery(queries.allWorkflows());
  const { spaceById } = useMonitoringSpaces();
  const [deletion, setDeletion] = useState<StorageDeletion | null>(null);
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const [retention, setRetention] = useState<{
    workflowId: string;
    retentionSeconds: number;
  } | null>(null);
  const drillUrl = (key: string) =>
    makeUrl(routes.modal.storageUsage, {
      path: drillPath(key),
      dir: "",
    });
  const tileByKey = new Map(level.tiles.map((tile) => [tile.key, tile]));
  const workflowUrl = ({
    spaceId,
    workflowId,
    branchId,
  }: StorageWorkflowLink) =>
    makeUrl(
      routes.space.workflow,
      { spaceId, workflowId, branchId: branchId ?? "" },
      { inheritParams: false }
    );
  // A tile with nothing below it opens its workflow instead.
  const tileUrl = (key: string) => {
    const tile = tileByKey.get(key);
    if (!tile) return undefined;
    if (tile.drillable) return drillUrl(key);
    return tile.workflow ? workflowUrl(tile.workflow) : undefined;
  };
  const workflowById = new Map(
    workflows.map((workflow) => [workflow.id, workflow])
  );
  const workflowOf = (key: string) => {
    const link = tileByKey.get(key)?.workflow;
    return level.view === "workflow" && link
      ? workflowById.get(link.workflowId)
      : undefined;
  };
  const retentionOf = (key: string) =>
    workflowOf(key)?.retentionSeconds ?? null;
  const tileIcon = (key: string) => {
    const workflow = workflowOf(key);
    if (workflow) return <SmallWorkflowBadge workflow={workflow} />;
    return (
      <Icon
        name={rowIcon(level.view, key, spaceById.get(key)?.mode)}
        size={16}
        color="var(--text-primary)"
      />
    );
  };
  const tileActions = (key: string): StorageAction[] => {
    const tile = tileByKey.get(key);
    if (!tile) return [];
    const actions: StorageAction[] = [];
    const { workflow, volume } = tile;
    if (workflow && tile.drillable) {
      actions.push({
        to: workflowUrl(workflow),
        label: "Open workflow",
        icon: "arrow-up-right",
      });
    }
    const retentionSeconds = retentionOf(key);
    if (
      retentionSeconds !== null &&
      workflow &&
      canEditRetention(workflow.spaceId)
    ) {
      const { workflowId } = workflow;
      actions.push({
        onPress: () => setRetention({ workflowId, retentionSeconds }),
        label: "Change retention",
        icon: "clock",
      });
    }
    if (volume && canDelete(volume)) {
      actions.push({
        onPress: () => setDeletion({ target: volume }),
        label: "Delete volume data",
        icon: "trash",
      });
    }
    return actions;
  };
  const retentionNote = (key: string) => {
    const seconds = retentionOf(key);
    return seconds === null
      ? undefined
      : `Keeps runs ${formatRetention(seconds)}`;
  };
  // Rows folded into the treemap’s “N more” tile highlight with it, and it with them.
  const shownKeys = new Set(level.segments.map((segment) => segment.key));
  const highlightedTile =
    hoveredKey === null || shownKeys.has(hoveredKey)
      ? hoveredKey
      : OTHER_SPACES_KEY;
  const isRowHighlighted = (key: string) =>
    hoveredKey === key ||
    (hoveredKey === OTHER_SPACES_KEY && !shownKeys.has(key));
  const levelKeys = level.crumbs.at(-1)?.keys ?? [];
  const levelKey = storagePath(levelKeys);
  const onDrill = (key: string) =>
    recordInteraction("workflows.storage_usage_drill", {
      targetType: level.view ?? undefined,
      targetId: key,
    });

  return (
    <ModalBody>
      <StorageCard data-split="true">
        <Main>
          {header}
          <TreemapArea>
            {level.segments.length === 0 ? (
              <Treemap segments={[]} placeholder />
            ) : (
              <Treemap
                segments={level.segments}
                formatValue={formatBytes}
                segmentUrl={(segment) =>
                  segment.key === OTHER_SPACES_KEY
                    ? undefined
                    : tileUrl(segment.key)
                }
                onSegmentNavigate={(segment) => onDrill(segment.key)}
                zoomPath={levelKeys}
                highlightedKey={highlightedTile}
                onSegmentHover={setHoveredKey}
              />
            )}
          </TreemapArea>
        </Main>
        <PanelTransition viewKey={levelKey} depth={levelKeys.length}>
          <StorageSidebar
            key={levelKey}
            title={level.view ? SIDEBAR_TITLES[level.view] : "Storage"}
            level={level}
            tileUrl={tileUrl}
            tileActions={tileActions}
            tileNote={retentionNote}
            tileIcon={tileIcon}
            isHighlighted={isRowHighlighted}
            onHover={setHoveredKey}
            onDrill={onDrill}
          />
        </PanelTransition>
      </StorageCard>
      {deletion && (
        <StorageDeleteConfirm
          deletion={deletion}
          onClose={() => setDeletion(null)}
        />
      )}
      {retention && (
        <WorkflowRetentionModal
          workflowId={retention.workflowId}
          retentionSeconds={retention.retentionSeconds}
          onClose={() => setRetention(null)}
        />
      )}
    </ModalBody>
  );
}
